/**
 * Content Manager - Configuration Service
 *
 * Service layer for application configuration operations using AWS API Gateway.
 */

import { apiClient, isAWSConfigured } from '../lib/awsConfig';
import type { AppConfig, ApiResponse } from '../types';
import { DEFAULT_APP_CONFIG } from '../constants';

// =============================================================================
// CONFIG SERVICE
// =============================================================================

export const configService = {
  /**
   * Fetch application configuration
   */
  async fetch(): Promise<ApiResponse<AppConfig>> {
    if (!isAWSConfigured()) {
      return { data: DEFAULT_APP_CONFIG, error: null };
    }

    try {
      const { data, error } = await apiClient.get<AppConfig>('/config');

      if (error) throw error;

      return {
        data: data || DEFAULT_APP_CONFIG,
        error: null,
      };
    } catch (error) {
      console.error('Error fetching config:', error);
      return { data: DEFAULT_APP_CONFIG, error: error as Error };
    }
  },

  /**
   * Update application configuration
   */
  async update(updates: Partial<AppConfig>): Promise<ApiResponse<AppConfig>> {
    if (!isAWSConfigured()) {
      return { data: null, error: new Error('AWS not configured') };
    }

    try {
      const { data, error } = await apiClient.patch<AppConfig>('/config', updates);

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error('Error updating config:', error);
      return { data: null, error: error as Error };
    }
  },

  /**
   * Reset configuration to defaults
   */
  async reset(): Promise<ApiResponse<AppConfig>> {
    return this.update({
      accentColour: DEFAULT_APP_CONFIG.accentColour,
      theme: DEFAULT_APP_CONFIG.theme,
      hidePerformanceTab: DEFAULT_APP_CONFIG.hidePerformanceTab,
      contentTypes: DEFAULT_APP_CONFIG.contentTypes,
      platforms: DEFAULT_APP_CONFIG.platforms,
      contentSilos: DEFAULT_APP_CONFIG.contentSilos,
      platformBrands: DEFAULT_APP_CONFIG.platformBrands,
      csvFieldMapping: DEFAULT_APP_CONFIG.csvFieldMapping,
    });
  },

  /**
   * Add a content type
   */
  async addContentType(type: string): Promise<ApiResponse<AppConfig>> {
    const { data: current } = await this.fetch();
    if (!current) {
      return { data: null, error: new Error('Could not fetch current config') };
    }

    if (current.contentTypes.includes(type)) {
      return { data: current, error: null };
    }

    return this.update({
      contentTypes: [...current.contentTypes, type],
    });
  },

  /**
   * Remove a content type
   */
  async removeContentType(type: string): Promise<ApiResponse<AppConfig>> {
    const { data: current } = await this.fetch();
    if (!current) {
      return { data: null, error: new Error('Could not fetch current config') };
    }

    return this.update({
      contentTypes: current.contentTypes.filter((t) => t !== type),
    });
  },

  /**
   * Add a platform
   */
  async addPlatform(platform: string): Promise<ApiResponse<AppConfig>> {
    const { data: current } = await this.fetch();
    if (!current) {
      return { data: null, error: new Error('Could not fetch current config') };
    }

    if (current.platforms.includes(platform)) {
      return { data: current, error: null };
    }

    return this.update({
      platforms: [...current.platforms, platform],
    });
  },

  /**
   * Remove a platform
   */
  async removePlatform(platform: string): Promise<ApiResponse<AppConfig>> {
    const { data: current } = await this.fetch();
    if (!current) {
      return { data: null, error: new Error('Could not fetch current config') };
    }

    const { [platform]: _, ...remainingBrands } = current.platformBrands;

    return this.update({
      platforms: current.platforms.filter((p) => p !== platform),
      platformBrands: remainingBrands,
    });
  },

  /**
   * Add a content silo
   */
  async addSilo(silo: string): Promise<ApiResponse<AppConfig>> {
    const { data: current } = await this.fetch();
    if (!current) {
      return { data: null, error: new Error('Could not fetch current config') };
    }

    if (current.contentSilos.includes(silo)) {
      return { data: current, error: null };
    }

    return this.update({
      contentSilos: [...current.contentSilos, silo],
    });
  },

  /**
   * Remove a content silo
   */
  async removeSilo(silo: string): Promise<ApiResponse<AppConfig>> {
    const { data: current } = await this.fetch();
    if (!current) {
      return { data: null, error: new Error('Could not fetch current config') };
    }

    return this.update({
      contentSilos: current.contentSilos.filter((s) => s !== silo),
    });
  },
};

export default configService;
